// Cross-tab coordination for the refresh_token exchange.
//
// The auth server rotates refresh tokens with a reuse leeway of zero: redeeming
// a refresh token that was already exchanged revokes the whole session chain.
// A per-tab single-flight is therefore not enough — two tabs that get a 401 at
// the same moment would both redeem the same refresh token. Everything here is
// kept free of axios / app imports so it can be unit tested in isolation.

export const TOKEN_REFRESH_LOCK_NAME = 'ics-token-refresh'

const STORAGE_LOCK_PREFIX = 'ICS_LOCK:'
// How long to wait for another tab before giving up and running unlocked.
const DEFAULT_ACQUIRE_TIMEOUT_MS = 10000
// A storage lock older than this is treated as abandoned (tab closed mid-refresh).
const DEFAULT_LOCK_TTL_MS = 15000
const DEFAULT_POLL_MS = 50
const DEFAULT_SETTLE_MS = 25
// A token renewed by another tab is only reused if it still has this much life.
const MIN_REMAINING_ACCESS_TOKEN_SECONDS = 10

interface LockManagerLike {
  request: (
    name: string,
    options: { signal?: AbortSignal },
    callback: () => Promise<any>
  ) => Promise<any>
}

export interface CrossTabLockOptions {
  acquireTimeoutMs?: number
  lockTtlMs?: number
  pollMs?: number
  settleMs?: number
  // Pass null to force the localStorage fallback (tests / unsupported browsers).
  locks?: LockManagerLike | null
  storage?: Storage | null
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

function getNativeLocks(): LockManagerLike | null {
  if (typeof navigator === 'undefined') {
    return null
  }
  const locks = (navigator as any).locks
  return locks && typeof locks.request === 'function' ? locks : null
}

function getDefaultStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

async function runWithNativeLock<T>(
  locks: LockManagerLike,
  name: string,
  task: () => Promise<T>,
  acquireTimeoutMs: number
): Promise<T> {
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null
  let acquired = false
  const timer = controller
    ? setTimeout(() => controller.abort(), acquireTimeoutMs)
    : null

  try {
    return await locks.request(
      name,
      controller ? { signal: controller.signal } : {},
      async () => {
        acquired = true
        if (timer) {
          clearTimeout(timer)
        }
        return task()
      }
    )
  } catch (error) {
    if (acquired) {
      throw error
    }
    // The lock was never granted (wait timed out, or locks are unavailable in
    // this context). Fail open: a hung tab must not block every other tab.
    return task()
  } finally {
    if (timer) {
      clearTimeout(timer)
    }
  }
}

function readStorageLock(
  storage: Storage,
  key: string
): { owner: string; expiresAt: number } | null {
  const raw = storage.getItem(key)
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed.owner === 'string' &&
      typeof parsed.expiresAt === 'number'
    ) {
      return parsed
    }
  } catch {
    // Corrupt entry — treat as no lock
  }
  return null
}

async function acquireStorageLock(
  storage: Storage,
  key: string,
  owner: string,
  options: Required<
    Pick<
      CrossTabLockOptions,
      'acquireTimeoutMs' | 'lockTtlMs' | 'pollMs' | 'settleMs'
    >
  >
): Promise<boolean> {
  const deadline = Date.now() + options.acquireTimeoutMs

  while (Date.now() < deadline) {
    let claimed = false
    try {
      const current = readStorageLock(storage, key)
      if (!current || current.expiresAt <= Date.now()) {
        storage.setItem(
          key,
          JSON.stringify({ owner, expiresAt: Date.now() + options.lockTtlMs })
        )
        claimed = true
      }
    } catch {
      // Storage unavailable (private mode / quota) — run unlocked
      return false
    }

    if (claimed) {
      // localStorage has no compare-and-swap: give a competing tab's write
      // time to land, then check which tab actually owns the lock.
      await sleep(options.settleMs + Math.random() * options.settleMs)
      try {
        if (readStorageLock(storage, key)?.owner === owner) {
          return true
        }
      } catch {
        return false
      }
      continue
    }

    await sleep(options.pollMs)
  }

  return false
}

function releaseStorageLock(storage: Storage, key: string, owner: string) {
  try {
    if (readStorageLock(storage, key)?.owner === owner) {
      storage.removeItem(key)
    }
  } catch {
    // Nothing to do — the entry expires on its own
  }
}

async function runWithStorageLock<T>(
  storage: Storage | null,
  name: string,
  task: () => Promise<T>,
  options: Required<
    Pick<
      CrossTabLockOptions,
      'acquireTimeoutMs' | 'lockTtlMs' | 'pollMs' | 'settleMs'
    >
  >
): Promise<T> {
  if (!storage) {
    return task()
  }
  const key = STORAGE_LOCK_PREFIX + name
  const owner = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  const acquired = await acquireStorageLock(storage, key, owner, options)

  try {
    return await task()
  } finally {
    if (acquired) {
      releaseStorageLock(storage, key, owner)
    }
  }
}

/**
 * Runs `task` while holding a lock shared by every tab of this origin. Uses the
 * Web Locks API when available, otherwise a best-effort localStorage lock.
 * Never blocks forever: if the lock cannot be obtained in time the task still runs.
 */
export async function withCrossTabLock<T>(
  name: string,
  task: () => Promise<T>,
  options: CrossTabLockOptions = {}
): Promise<T> {
  const acquireTimeoutMs =
    options.acquireTimeoutMs ?? DEFAULT_ACQUIRE_TIMEOUT_MS
  const locks = 'locks' in options ? options.locks : getNativeLocks()

  if (locks) {
    return runWithNativeLock(locks, name, task, acquireTimeoutMs)
  }

  const storage = 'storage' in options ? options.storage : getDefaultStorage()
  return runWithStorageLock(storage, name, task, {
    acquireTimeoutMs,
    lockTtlMs: options.lockTtlMs ?? DEFAULT_LOCK_TTL_MS,
    pollMs: options.pollMs ?? DEFAULT_POLL_MS,
    settleMs: options.settleMs ?? DEFAULT_SETTLE_MS,
  })
}

export interface HeldTokenState {
  refreshToken?: string | null
  tenantId?: string | null
  userName?: string | null
}

export type RefreshDecision =
  | { action: 'adopt'; accessToken: string }
  | { action: 'refresh'; syncFromStorage: boolean }

/**
 * Decides, once the lock is held, whether this tab still has to redeem its
 * refresh token. `storedAccountRaw` is the persisted `currentAccount` entry.
 *   - adopt: another tab already renewed the token and it is still valid.
 *   - refresh + syncFromStorage: storage holds a newer refresh token than this
 *     tab, but its access token is expired — refresh with the stored one.
 *   - refresh: nothing changed (or storage is unusable / belongs to another user).
 */
export function decideRefreshAfterLock(
  held: HeldTokenState,
  storedAccountRaw: string | null,
  nowSeconds: number
): RefreshDecision {
  const refreshAsIs: RefreshDecision = {
    action: 'refresh',
    syncFromStorage: false,
  }
  if (!held?.refreshToken || !storedAccountRaw) {
    return refreshAsIs
  }

  try {
    const persisted = JSON.parse(storedAccountRaw)
    if (!persisted || typeof persisted.oidcUser !== 'string') {
      return refreshAsIs
    }
    // Another tab may have switched account — never adopt someone else's token
    if (
      (persisted.tenantId ?? null) !== (held.tenantId ?? null) ||
      (persisted.userName ?? null) !== (held.userName ?? null)
    ) {
      return refreshAsIs
    }

    const oidcUser = JSON.parse(persisted.oidcUser)
    const storedRefreshToken = oidcUser?.refresh_token
    if (!storedRefreshToken || storedRefreshToken === held.refreshToken) {
      return refreshAsIs
    }

    const expiresAt =
      typeof oidcUser.expires_at === 'number'
        ? oidcUser.expires_at
        : persisted.expireAt
    if (
      typeof oidcUser.access_token === 'string' &&
      oidcUser.access_token &&
      typeof expiresAt === 'number' &&
      expiresAt - nowSeconds > MIN_REMAINING_ACCESS_TOKEN_SECONDS
    ) {
      return { action: 'adopt', accessToken: oidcUser.access_token }
    }

    return { action: 'refresh', syncFromStorage: true }
  } catch {
    return refreshAsIs
  }
}

export interface CrossTabRefreshHandlers {
  // Token state this tab holds in memory, read BEFORE waiting for the lock
  getHeld: () => HeldTokenState
  // Raw persisted `currentAccount` entry, read AFTER the lock is held
  readStoredAccount: () => string | null
  // Pull the persisted account into this tab's in-memory state
  syncFromStorage: () => void
  // Apply an access token that another tab already obtained
  adopt: (accessToken: string) => void
  // Redeem the refresh token; resolves with the new access token
  refresh: () => Promise<string | undefined>
}

/**
 * Refreshes the access token at most once across all tabs: takes the cross-tab
 * lock, re-reads the persisted token, and skips the exchange when another tab
 * renewed it while this one was waiting.
 */
export function refreshTokenAcrossTabs(
  handlers: CrossTabRefreshHandlers,
  lockOptions?: CrossTabLockOptions
): Promise<string | undefined> {
  let held: HeldTokenState = {}
  try {
    held = handlers.getHeld() ?? {}
  } catch {
    // Unreadable in-memory state — fall through to a plain refresh
  }

  return withCrossTabLock(
    TOKEN_REFRESH_LOCK_NAME,
    async () => {
      let storedAccountRaw: string | null = null
      try {
        storedAccountRaw = handlers.readStoredAccount()
      } catch {
        // Storage unavailable — fall through to a plain refresh
      }

      const decision = decideRefreshAfterLock(
        held,
        storedAccountRaw,
        Math.floor(Date.now() / 1000)
      )

      if (decision.action === 'adopt') {
        handlers.syncFromStorage()
        handlers.adopt(decision.accessToken)
        return decision.accessToken
      }

      if (decision.syncFromStorage) {
        handlers.syncFromStorage()
      }
      return handlers.refresh()
    },
    lockOptions
  )
}
